import { useEffect, useId, useSyncExternalStore } from "react"

/*
 * Whether a full-screen task is open on the compact layout: Search, the Composer, an EditorSheet.
 * The shell's tab bar steps aside while one is, so the task has the whole screen and nothing
 * navigates away from under it. Each task says so for itself with `useFullScreenTask`, for as long
 * as it is open, wherever it renders.
 */

/** The tasks open now, each by its own id. */
const open = new Set<string>()
const listeners = new Set<() => void>()

const told = () => listeners.forEach((listener) => listener())

/**
 * Records that `task` is open while `active`, and gives back what takes the record away again, as
 * an effect's cleanup does when the task closes or goes.
 */
export function markFullScreenTask(task: string, active: boolean) {
  if (!active) return () => undefined
  open.add(task)
  told()
  return () => {
    open.delete(task)
    told()
  }
}

/** Says this task fills the screen while `active`. */
export function useFullScreenTask(active: boolean) {
  const task = useId()
  useEffect(() => markFullScreenTask(task, active), [task, active])
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => void listeners.delete(listener)
}

/** Whether any full-screen task is open now. */
export const fullScreenTaskOpen = () => open.size > 0

/** Whether any full-screen task is open, kept current as tasks open and close. */
export function useFullScreenTaskOpen() {
  return useSyncExternalStore(subscribe, fullScreenTaskOpen)
}
